/* ===========================================================================
   Catalog bulk selector - enforce BSS quantity-increment rules.

   THE PROBLEM
   The BSS app holds increment rules (order in multiples of 6 / 10 / 5). On the
   product page they all work. On the catalog grid only SOME of them do.

   The difference is how each rule was built in the app:

     Rule 8015 "Planners"        condition type 4 -> product_variants   works
     Rule 8016 "Grid boards"     condition type 4 -> product_variants   works
     Rule 8126 "Pens/sketchpads" condition type 1 -> product_ids        does NOT

   The catalog's bulk selector renders variant-level inputs
   (data-quantity-variant-id="...") with no product id anywhere in the card, so
   a variant-keyed rule matches immediately while a product-keyed rule has
   nothing to match against and is skipped.

   Note the server HTML always ships step="1" min="0"; BSS rewrites it client
   side. So there is no Liquid fix - it has to happen in JS after the app runs.

   THE FIX
   Resolve product-keyed rules ourselves. BSS_B2B.storage.variantStorage maps
   variant id -> productId, which is the link the app fails to follow here. For
   every quantity input we look up its variant, find the matching rule by
   EITHER variant id or product id, then set step/min and round typed values.

   This is additive: where the app already set the right step (planners, grid
   boards) we compute the same value, so nothing changes. It only fills gaps.

   Longer term the cleaner fix is to rebuild rule 8126 in the BSS app targeting
   variants rather than products - then this script becomes a no-op safety net.
   =========================================================================== */
(function () {
  var MARK = 'planoraQtyRule';

  function parseIdList(str) {
    if (!str) return [];
    return String(str).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function getRules() {
    var qi = window.BSS_B2B && window.BSS_B2B.qi;
    if (!qi || qi.status !== true) return [];
    return qi.qiRules || qi.rules || [];
  }

  function productIdForVariant(variantId) {
    try {
      var store = window.BSS_B2B && window.BSS_B2B.storage && window.BSS_B2B.storage.variantStorage;
      if (!store) return null;
      var v = store.get(Number(variantId)) || store.get(String(variantId));
      return v && v.productId ? String(v.productId) : null;
    } catch (e) {
      return null;
    }
  }

  /* Variant-keyed rules are checked first, mirroring the app's own precedence:
     a rule naming this exact variant is more specific than one naming its
     parent product. */
  function incrementFor(variantId) {
    var rules = getRules();
    if (!rules.length) return 0;

    var vid = String(variantId);
    var pid = productIdForVariant(variantId);

    for (var i = 0; i < rules.length; i++) {
      if (parseIdList(rules[i].product_variants).indexOf(vid) !== -1) {
        return Number(rules[i].qty_increment) || 0;
      }
    }
    if (pid) {
      for (var j = 0; j < rules.length; j++) {
        if (parseIdList(rules[j].product_ids).indexOf(pid) !== -1) {
          return Number(rules[j].qty_increment) || 0;
        }
      }
    }
    return 0;
  }

  function roundToIncrement(value, inc) {
    if (!inc || inc < 2) return value;
    if (value <= 0) return 0;
    var rounded = Math.round(value / inc) * inc;
    return rounded < inc ? inc : rounded;
  }

  function applyTo(input) {
    var vid = input.getAttribute('data-quantity-variant-id');
    if (!vid) return;

    var inc = incrementFor(vid);
    if (!inc || inc < 2) return;

    if (input.getAttribute('step') !== String(inc)) {
      input.setAttribute('step', String(inc));
    }
    if (Number(input.getAttribute('min')) !== inc && Number(input.value) !== 0) {
      input.setAttribute('min', String(inc));
    }

    if (input.dataset[MARK]) return;
    input.dataset[MARK] = String(inc);

    input.addEventListener('change', function () {
      var current = parseInt(input.value, 10);
      if (isNaN(current)) return;
      var fixed = roundToIncrement(current, inc);
      if (fixed !== current) {
        input.value = fixed;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  function scan() {
    document.querySelectorAll('input[data-quantity-variant-id]').forEach(applyTo);
  }

  function start() {
    scan();
    var observer = new MutationObserver(function () { scan(); });
    observer.observe(document.body, { childList: true, subtree: true });
    [400, 1200, 2500].forEach(function (ms) { setTimeout(scan, ms); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
